import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Header from './Header'; // Import Header component
import Footer from './Footer'; // Import Footer component

const JoinRequest = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    clinicHospitalName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: '',
    specialization: '',
    licenseNumber: '',
    issuingAuthority: '',
    yearsOfExperience: '',
    education: '',
    certifications: '',
    address: '',
    cityCountry: '',
    postalCode: '',
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [formValid, setFormValid] = useState(false);

  // Subscription state for the footer
  const [email, setEmail] = useState('');
  const [subscribeError, setSubscribeError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  useEffect(() => {
    const requiredFields = [
      'fullName',
      'email',
      'phoneNumber',
      'specialization',
      'address',
      'cityCountry',
    ];
    const isValid = requiredFields.every((field) => formData[field].trim() !== '');
    setFormValid(isValid);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      setError('Please fill out all required fields.');
      return;
    }
    setSuccess('');
    setError('');
    try {
      await addDoc(collection(db, 'joinRequests'), {
        ...formData,
        createdAt: new Date().toISOString(),
      });
      setSuccess('Join request submitted successfully!');
      setFormData({
        fullName: '',
        clinicHospitalName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        gender: '',
        specialization: '',
        licenseNumber: '',
        issuingAuthority: '',
        yearsOfExperience: '',
        education: '',
        certifications: '',
        address: '',
        cityCountry: '',
        postalCode: '',
      });
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      console.error('Error submitting join request:', err);
      setError('Failed to submit join request: ' + err.message);
    }
  };

  // Handle subscription for the footer
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSubscribeError('Please enter a valid email address.');
      return;
    }
    try {
      await addDoc(collection(db, 'subscribers'), { email, timestamp: new Date() });
      setSubscribeError('');
      alert(`Subscribed with email: ${email}`);
      setEmail('');
    } catch (err) {
      setSubscribeError('Failed to subscribe: ' + err.message);
    }
  };

  // Handle login redirect for the header
  const handleLoginRedirect = () => {
    navigate('/login');
  };

  // Animation variant for the footer
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
  };

  return (
    <div className="min-h-screen bg-[#d3d4d6] text-black flex flex-col items-center justify-between font-['Noto Sans']">
      {/* Header */}
      <Header handleLoginRedirect={handleLoginRedirect} fadeIn={fadeIn} />

      {/* Main Content */}
      <div className="join-request p-6 font-['Noto Sans'] max-w-3xl mx-auto w-full flex-1">
        <h2 className="text-4xl font-bold mb-6 text-gray-600">Join Request Form</h2>
        {success && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
          <h3 className="text-2xl font-bold mb-4 text-gray-600">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-600 mb-1">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., John Doe"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Clinic/Hospital/Organization Name</label>
              <input
                type="text"
                name="clinicHospitalName"
                value={formData.clinicHospitalName}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., City Hospital"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., john.doe@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., +1234567890"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-4 mt-6 text-gray-600">Professional Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-600 mb-1">Specialization *</label>
              <input
                type="text"
                name="specialization"
                value={formData.specialization}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Cardiologist"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Medical License Number</label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., MLN123456"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Issuing Authority of Medical License</label>
              <input
                type="text"
                name="issuingAuthority"
                value={formData.issuingAuthority}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Medical Board of California"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Years of Experience</label>
              <input
                type="number"
                name="yearsOfExperience"
                value={formData.yearsOfExperience}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 10"
                min="0"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Educational Background</label>
              <textarea
                name="education"
                value={formData.education}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., MBBS, Harvard Medical School, 2010"
                rows="3"
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Certifications & Additional Qualifications</label>
              <textarea
                name="certifications"
                value={formData.certifications}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Board Certified in Cardiology, 2015"
                rows="3"
              />
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-4 mt-6 text-gray-600">Location & Address</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-600 mb-1">Clinic/Hospital Address *</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 123 Main St, Suite 101"
                rows="3"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">City & Country *</label>
              <input
                type="text"
                name="cityCountry"
                value={formData.cityCountry}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., New York, USA"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">Postal Code</label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 10001"
              />
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              type="submit"
              disabled={!formValid}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors duration-300 ${
                formValid
                  ? 'bg-green-700 text-white hover:bg-gray-600'
                  : 'bg-gray-400 text-gray-700 cursor-not-allowed'
              }`}
            >
              Submit Join Request
            </button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <Footer
        email={email}
        setEmail={setEmail}
        handleSubscribe={handleSubscribe}
        subscribeError={subscribeError}
        fadeIn={fadeIn}
      />
    </div>
  );
};

export default JoinRequest;